import React from 'react';

import {
  Alert,
  Avatar,
  Column,
  Divider,
  Field,
  FieldEditor,
  FieldGroup,
  FieldGroupEditor,
  GotoBlock,
  HistoricalUnitAssignment,
  ObjectField,
  Repeater,
  RepeaterAddItem,
  RequirentCompliant,
  Row,
  Section,
  SectionCollapse,
  SectionWrapper,
  Tab,
  TabContent,
  TrainingTaskDocument,
  TrainingTaskTimeline,
  VerticalInput,
} from '../components/form-viewer/object-types';

import {
  AutoComplete,
  Checkbox,
  Date as DatePicker,
  DateTime,
  Duration,
  File,
  Hyperlink,
  InputMasked,
  MathField,
  MultiSelect,
  Number as NumberPicker,
  Select,
  SingleUpload,
  StaticContent,
  Switch,
  Text,
  Textarea,
  Time,
  Upload,
} from '../components/form-viewer/field-types';

import BodyDiagram from '../components/body-diagram';

import * as FORM_COMPONENT_TYPES from './layoutComponentTypes';
import * as FORM_FIELD_TYPES from './fieldTypes';

const PlainText = props => {
  const { options: { className = '' } = {} } = props;
  return <div className={className}>{props.text || 'component'}</div>;
};

export const LAYOUT_TYPES = {
  [FORM_COMPONENT_TYPES.FIELD_GROUP]: FieldGroup,
  [FORM_COMPONENT_TYPES.INJURIES_BODY]: BodyDiagram,
  [FORM_COMPONENT_TYPES.PLAN_TEXT]: PlainText,
  [FORM_COMPONENT_TYPES.REPEATER_ACTION]: RepeaterAddItem,
  [FORM_COMPONENT_TYPES.REQUIREMENT_COMPLIANT]: RequirentCompliant,
  [FORM_COMPONENT_TYPES.SECTION_COLLAPSE]: SectionCollapse,
  [FORM_COMPONENT_TYPES.SECTION_WRAPPER]: SectionWrapper,
  [FORM_COMPONENT_TYPES.TRAINING_TASK_DOCUMENT]: TrainingTaskDocument,
  [FORM_COMPONENT_TYPES.TRAINING_TASK_TIMELIME]: TrainingTaskTimeline,
  [FORM_COMPONENT_TYPES.ALERT_MESSAGE]: Alert,
  [FORM_COMPONENT_TYPES.COLUMN]: Column,
  [FORM_COMPONENT_TYPES.DIVIDER]: Divider,
  [FORM_COMPONENT_TYPES.FIELD]: Field,
  [FORM_COMPONENT_TYPES.GROUP]: ObjectField,
  [FORM_COMPONENT_TYPES.REPEATER]: Repeater,
  [FORM_COMPONENT_TYPES.ROW]: Row,
  [FORM_COMPONENT_TYPES.SECTION]: Section,
  [FORM_COMPONENT_TYPES.TAB]: Tab,
  [FORM_COMPONENT_TYPES.TABS]: TabContent,
  [FORM_COMPONENT_TYPES.UPLOAD]: Upload,
  [FORM_COMPONENT_TYPES.SINGLE_UPLOAD]: SingleUpload,
  [FORM_COMPONENT_TYPES.GOTO_BLOCK]: GotoBlock,
  [FORM_COMPONENT_TYPES.HISTORICAL_UNIT_ASSIGNMENT]: HistoricalUnitAssignment,
};

export const SELF_SERVICE_LAYOUT_TYPES = {
  ...LAYOUT_TYPES,
  [FORM_COMPONENT_TYPES.FIELD]: FieldEditor,
  [FORM_COMPONENT_TYPES.FIELD_GROUP]: FieldGroupEditor,
};

export const FORM_ELEMENTS = [
  // This one was deprecated but we'll add it here to keep existing templates support.
  FORM_COMPONENT_TYPES.FIELD_GROUP,
  FORM_COMPONENT_TYPES.GROUP,
  FORM_COMPONENT_TYPES.INJURIES_BODY,
  FORM_COMPONENT_TYPES.REPEATER,
  FORM_COMPONENT_TYPES.ROW,
  FORM_COMPONENT_TYPES.SECTION,
  FORM_COMPONENT_TYPES.SECTION_WRAPPER,
  FORM_COMPONENT_TYPES.SINGLE_UPLOAD,
  FORM_COMPONENT_TYPES.UPLOAD,
];

export const FORM_ELEMENTS_FOR_COPY_PASTE = FORM_ELEMENTS.filter(
  type => type !== FORM_COMPONENT_TYPES.INJURIES_BODY
);

export const FIELD_TYPES = {
  [FORM_FIELD_TYPES.DATE_TIME]: DateTime,
  [FORM_FIELD_TYPES.DYNAMIC_SELECT]: Select,
  [FORM_FIELD_TYPES.AUTOCOMPLETE]: AutoComplete,
  [FORM_FIELD_TYPES.CHECKBOX]: Checkbox,
  [FORM_FIELD_TYPES.DATE]: DatePicker,
  [FORM_FIELD_TYPES.FILE]: File,
  [FORM_FIELD_TYPES.MASKED.toLowerCase()]: InputMasked,
  [FORM_FIELD_TYPES.MULTISELECT]: MultiSelect,
  [FORM_FIELD_TYPES.NUMBER]: NumberPicker,
  [FORM_FIELD_TYPES.SELECT]: Select,
  [FORM_FIELD_TYPES.SWITCH]: Switch,
  [FORM_FIELD_TYPES.TEXT]: Text,
  [FORM_FIELD_TYPES.TEXTAREA]: Textarea,
  [FORM_FIELD_TYPES.TIME]: Time,
  [FORM_FIELD_TYPES.VERTICAL_INPUT]: VerticalInput,
  [FORM_FIELD_TYPES.AVATAR]: Avatar,
  [FORM_FIELD_TYPES.HYPERLINK]: Hyperlink,
  [FORM_FIELD_TYPES.STATIC_CONTENT]: StaticContent,
  [FORM_FIELD_TYPES.DURATION]: Duration,
  [FORM_FIELD_TYPES.MATH]: MathField,
};

export const FORBIDDEN_TEMPLATE_NAMES = ['Review Notes Template'];

export function validateTemplateName(name = '') {
  return FORBIDDEN_TEMPLATE_NAMES.map(n => n.toLowerCase()).includes(name.toLowerCase().trim()) 
    ? 'This template name is not allowed. Please choose a different name.'
    : undefined;
}


export const emptyObject = {};
export const emptyArray = [];

export const FEATURE_FLAGS_COPY_PASTE = {
  M1: 'formBuilderCopyPasteM1::1.0',
  M2: 'formBuilderCopyPasteM2::1.0',
  M3: 'formBuilderCopyPasteM3::1.0',
};

export const ENTITY_KIND = {
  FIELD: 'field',
  ELEMENT: 'element',
  TAB: 'tab',
};

export const ENTITY_ORDER = {
  [ENTITY_KIND.FIELD]: 0,
  [ENTITY_KIND.ELEMENT]: 1,
  [ENTITY_KIND.TAB]: 2,
};

export function getCopyPasteFeatureScope(featureFlags) {
  const milestone1 = featureFlags?.includes(FEATURE_FLAGS_COPY_PASTE.M1);
  const milestone2 = featureFlags?.includes(FEATURE_FLAGS_COPY_PASTE.M2);
  const milestone3 = featureFlags?.includes(FEATURE_FLAGS_COPY_PASTE.M3);

  if (milestone3) {
    return ENTITY_KIND.TAB;
  } else if (milestone2) {
    return ENTITY_KIND.ELEMENT;
  } else if (milestone1) {
    return ENTITY_KIND.FIELD;
  }

  return null;
}

export function isWithinCopyPasteFeatureMilestone(type, featureFlags) {
  const entityScope = getCopyPasteFeatureScope(featureFlags);
  let currentKind;

  // If no feature flag is enabled, or type is invalid, return false
  if (!type || !entityScope) {
    return false;
  }

  if (type === FORM_COMPONENT_TYPES.TAB) {
    currentKind = ENTITY_KIND.TAB;
  } else if (Object.values(FORM_FIELD_TYPES).includes(type)) {
    currentKind = ENTITY_KIND.FIELD;
  } else if (FORM_ELEMENTS_FOR_COPY_PASTE.includes(type)) {
    currentKind = ENTITY_KIND.ELEMENT;
  } else {
    return false;
  }

  return ENTITY_ORDER[currentKind] <= ENTITY_ORDER[entityScope];
}
